<template>
    <div class="compare-result-list pa-text-gray-1100">
        <div v-if="isSame" class="pa-flex pa-flex-col pa-items-center pa-px-4 pa-pt-[40px]">
            <img src="../../../assets/images/compare-same.png" alt="">
            <p class="pa-mt-2 pa-text-center pa-text-gray-450">
                {{ t('patent-view.compareView.compareSame') }}
            </p>
        </div>
        <div v-else-if="isLoading" class="compare-result-list__loading pa-p-4">
            <div v-for="i in 2" :key="i">
                <span class="pa-inline-block pa-h-4 pa-w-1/2 pa-rounded pa-bg-green-75" />
                <span class="pa-mt-2 pa-inline-block pa-h-4 pa-w-full pa-rounded pa-bg-gray-30" />
                <span class="pa-mt-2 pa-inline-block pa-h-4 pa-w-full pa-rounded pa-bg-gray-30" />
                <div
                    v-if="i !== 2"
                    class="pa-my-2 pa-flex"
                >
                    <span class="pa-flex pa-flex-1 pa-items-center">
                        <span class="pa-h-[1px] pa-w-full pa-bg-gray-40" />
                    </span>
                    <span class="pa-mx-2 pa-text-xs pa-font-semibold pa-text-gray-90">VS</span>
                    <span class="pa-flex pa-flex-1 pa-items-center">
                        <span class="pa-h-[1px] pa-w-full pa-bg-gray-40" />
                    </span>
                </div>
            </div>
            <div class="pa-mt-2 pa-flex pa-items-center pa-justify-center">
                <PtIcon icon="SolidLoadingBars" />
                <span class="pa-ml-2 pa-text-gray-450">{{ t('patent-view.compareView.lookingForSimilarities') }}</span>
            </div>
        </div>
        <div v-else-if="isNodata">
            <PtNodata class="pa-mt-[40px]" :isShowTips="false" />
            <div class="pa-mt-2 pa-px-[60px] pa-leading-6 pa-text-gray-450">
                <p>{{ t('patent-view.compareView.compareNodata.nodata') }}</p>
                <ol>
                    <template v-if="isTimeout">
                        <li class="pa-flex pa-items-center">
                            <span class="pa-mr-2 pa-h-[4px] pa-w-[4px] pa-shrink-0 pa-rounded-full pa-bg-gray-450" />
                            <span>{{ t('patent-view.compareView.compareNodata.reason3') }}</span>
                        </li>
                    </template>
                    <template v-else>
                        <li class="pa-flex pa-items-center">
                            <span class="pa-mr-2 pa-h-[4px] pa-w-[4px] pa-shrink-0 pa-rounded-full pa-bg-gray-450" />
                            <span>{{ t('patent-view.compareView.compareNodata.reason1') }}</span>
                        </li>
                        <li class="pa-flex pa-items-center">
                            <span class="pa-mr-2 pa-h-[4px] pa-w-[4px] pa-shrink-0 pa-rounded-full pa-bg-gray-450" />
                            <span>{{ t('patent-view.compareView.compareNodata.reason2') }}</span>
                        </li>
                    </template>
                </ol>
                <p>{{ t('patent-view.compareView.compareNodata.resolve') }}</p>
            </div>
        </div>
        <template v-else>
            <div v-if="compareResultList?.length" class="pa-px-4 pa-py-2.5 pa-font-semibold pa-leading-5">
                <span>{{ t('patent-view.compareView.similarCount', { count: compareResultList?.length }) }}</span>
            </div>
            <div
                v-for="(item, index) in displayResultList"
                :key="index"
                class="compare-result-list__item pa-border-t pa-border-solid pa-border-gray-40 pa-p-4"
            >
                <CompareSimilarTag
                    :similarity="item.similarity"
                >
                    <template #num>
                        <span>{{ item.paragraph_id }}</span>
                    </template>
                </CompareSimilarTag>
                <div class="pa-mt-2 pa-overflow-hidden pa-rounded pa-bg-gray-20">
                    <div
                        class="pa-cursor-pointer pa-p-2 hover:pa-bg-gray-30"
                        @click="scrollToSimilar({
                            num:item.paragraph_id,
                            index: 1
                        })"
                    >
                        <p class="pa-text-xs pa-text-green-600">
                            <!--<span>{{ item.src.PN }} </span>-->
                            <span>{{ t('patent-view.compareView.techParagraph') }}</span>
                        </p>
                        <p class="pa-mt-1 pa-line-clamp-3 pa-text-ellipsis pa-leading-5" :title="item.src.similar_part">
                            <span v-html="item.src.similar_part" />
                        </p>
                    </div>
                    <div class="pa-mx-2 pa-flex">
                        <span class="pa-flex pa-flex-1 pa-items-center">
                            <span class="pa-h-[1px] pa-w-full pa-bg-gray-40" />
                        </span>
                        <span class="pa-mx-2 pa-text-xs pa-font-semibold pa-text-gray-90">VS</span>
                        <span class="pa-flex pa-flex-1 pa-items-center">
                            <span class="pa-h-[1px] pa-w-full pa-bg-gray-40" />
                        </span>
                    </div>
                    <div
                        v-for="(target, tIndex) in (item.showAll ? item.target : item.target.slice(0, DEFAULT_RESULT_LENGTH))"
                        :key="`target_${tIndex}`"
                        :title="t('patent-view.compareView.clickToSimilar')"
                        class="pa-cursor-pointer pa-p-2 hover:pa-bg-gray-30"
                        @click="scrollToSimilar({
                            num:item.paragraph_id,
                            index: tIndex + 1
                        })"
                    >
                        <p class="pa-text-xs pa-text-green-600">
                            <span>{{ tgtPN }}</span>
                            <span class="pa-ml-1">{{ t(`patent-view.compareView.${target.field_type}`) }}</span>
                        </p>
                        <p class="pa-mt-1 pa-line-clamp-3 pa-text-ellipsis pa-leading-5">
                            <span v-html="target.similar_part" />
                        </p>
                    </div>
                    <div
                        v-if="!item.showAll"
                        class="pa-flex pa-h-[36px] pa-cursor-pointer pa-items-center pa-justify-center hover:pa-bg-gray-30"
                        @click="item.showAll = true"
                    >
                        <PtIcon icon="SolidDoubleArrowDown" class="!pa-text-blue-500" />
                        <span class="pa-text-blue-500">{{ t('snapBusiness.commonMenuTabs.showMore') }}</span>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { watch, computed, ref } from 'vue';
import { useI18n } from '@/composables/use-i18n';
import PtNodata from '@/components/PtNodata.vue';
import { Icon as PtIcon } from 'patsnap-biz';
import CompareSimilarTag from './CompareSimilarTag.vue';
import { useCompareInit } from '@/composables/use-compare-init';
import { useCompareUrlState } from '@/composables/use-compare-url-state';
import { useCompareSource } from '@/composables/use-compare-source';
import type { ApiGetCompareOverviewRequest } from '@/infrastructure/api-functions/core-search-api/core-search-api';
import '@patsnap-ui/icon/assets/solid/LoadingBars.svg';
import _ from 'lodash';
import { useTranslationStates } from '@/composables/use-translation-states';
import { useSimilarParagraph } from '@/composables/use-similar-paragraph';
import { useSimilarParaScroll } from '@/composables/use-similar-para-scroll';
import { LangTypeMode } from '@/domains/translation/enums';
import { DEFAULT_LANGUAGE } from '@/domains/translation/constants';
import '@patsnap-ui/icon/assets/solid/DoubleArrowDown.svg';

interface DisplayResultList {
    src: {
        PN: string;
        similar_part: string;
        field_type: string;
    };
    target: {
        PN: string;
        similar_part: string;
        field_type: string;
    }[];
    similarity: number;
    part_id: number;
    paragraph_id: string;
    showAll: boolean;
}

const {
    isLoading,
    isNodata,
    isSame,
    isTimeout,

    deferred,

    compareResultList,
    textCompareLang,
    tgtPN,

    compareInit,
    resetCompareInitState,
    performFakeComparison,
} = useCompareInit();

const {
    compareId,
    leftId,
    rightId,
    isTargetEmpty,
    isSourceCustom,
    isSourceCustomText,
    isSourcePatent,
} = useCompareUrlState();

const { compareResultTextList } = useCompareSource();

const {
    switchTranslateLanguage,
    currentTranslateLanguages,
} = useTranslationStates();

const { t } = useI18n();

const { scrollToSimilarPara } = useSimilarParagraph();

const {
    changeSimilarParaNumSerialIdx,
    changeSimilarParaNum,
    getCurScrollInfo,
} = useSimilarParaScroll();

const DEFAULT_RESULT_LENGTH = 1;

// 两侧语言以左侧主专利为准
const currentPatentLang = computed(() => {
    return _.get(currentTranslateLanguages.value, 'left');
});

const displayResultList = ref<DisplayResultList[]>();
watch(compareResultList, () => {
    displayResultList.value = _.map(compareResultList.value, item => {
        return {
            ...item,
            showAll: item.target.length <= DEFAULT_RESULT_LENGTH,
        };
    });
}, { immediate: true });

const isTargetPatentChange = ref(false);

const scrollToSimilar = ({
    num,
    index,
}: {
    num: string;
    index: number;
}) => {
    changeSimilarParaNum(num);
    changeSimilarParaNumSerialIdx(index);
    scrollToSimilarPara(getCurScrollInfo('left'));
    scrollToSimilarPara(getCurScrollInfo('right'));
};

// 只有左侧leftId为default或是专利id默认为语义检索文本过来的，需要执行compareInit
watch(rightId, async () => {
    // 重置所有状态参数
    resetCompareInitState();
    switchTranslateLanguage({
        side: 'both',
        lang: DEFAULT_LANGUAGE,
        langType: LangTypeMode.DEFAULT,
    });
    if (isTargetEmpty.value || isSourceCustom.value || isSame.value) {
        if (isSame.value) {
            await performFakeComparison();
        }
        if (!isTargetEmpty.value) {
            /*
            * 未执行compare-over的情况，需要手动resolve
            */
            deferred.value?.resolve({});
        }
        return;
    }
    isTargetPatentChange.value = true;
    // 右侧专利切换时，需要重置当前的翻译状态
    const params:ApiGetCompareOverviewRequest = {
        compare_id: compareId.value,
        target_patent_id: rightId.value,
    };
    if (isSourcePatent.value) {
        params.src_patent_id = leftId.value;
    }

    if (isSourceCustomText.value) {
        params.tech_text_list = compareResultTextList.value;
    }

    await compareInit(params);
    isTargetPatentChange.value = false;
}, { immediate: true });


// 当切换翻译时，需要重新调用compare init接口
watch(currentPatentLang, async () => {
    if (isTargetPatentChange.value) return;
    const params:ApiGetCompareOverviewRequest = {
        compare_id: compareId.value,
        target_patent_id: rightId.value,
        src_patent_id: leftId.value,
        text_compare_lang: textCompareLang.value,
    };
    if (currentPatentLang.value && currentPatentLang.value !== DEFAULT_LANGUAGE) {
        params.compare_lang = currentPatentLang.value;
    }
    await compareInit(params);
});

</script>

<style lang="scss">

</style>
